import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './Settings.css';

// تنظیمات پیش‌فرض — دقیقاً مطابق نسخه قبلی (server.js)
// این مقادیر زمانی استفاده می‌شوند که کاربر هیچ تنظیماتی در دیتابیس ندارد.
const DEFAULT_SETTINGS = [
  { key: 'bank_interest_rate',   value: 0.20 },
  { key: 'bourse_buy_comm',      value: 0.003712 },
  { key: 'bourse_sell_comm',     value: 0.003712 },
  { key: 'farabourse_buy_comm',  value: 0.003712 },
  { key: 'farabourse_sell_comm', value: 0.003712 },
  { key: 'leveragedfund_buy_comm',  value: 0.002345 },
  { key: 'leveragedfund_sell_comm', value: 0.0023 },
  { key: 'gold_buy_comm',        value: 0.0011 },
  { key: 'gold_sell_comm',       value: 0.0011 },
  { key: 'future_buy_comm',      value: 0.0006 },
  { key: 'future_sell_comm',     value: 0.0006 },
  { key: 'option_buy_comm',      value: 0.00103 },
  { key: 'option_sell_comm',     value: 0.00103 }
];

function Settings({ settings, onSettingsUpdate }) {
  const [settingsData, setSettingsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    if (settings && settings.length > 0) {
      setSettingsData(settings.map(s => ({ ...s, value: parseFloat(s.value) })));
    } else {
      fetchSettings();
    }
  }, [settings]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/settings');
      if (data && data.length > 0) {
        setSettingsData(data.map(s => ({ ...s, value: parseFloat(s.value) })));
      } else {
        // کاربر جدید بدون تنظیمات → بارگذاری مقادیر پیش‌فرض و ذخیره در دیتابیس
        setSettingsData(DEFAULT_SETTINGS.map(s => ({ ...s })));
        try {
          await api.put('/api/settings', DEFAULT_SETTINGS);
          if (onSettingsUpdate) onSettingsUpdate();
        } catch (seedError) {
          console.error('Error seeding default settings:', seedError);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      // در صورت خطا در ارتباط، حداقل مقادیر پیش‌فرض نمایش داده شوند
      setSettingsData(DEFAULT_SETTINGS.map(s => ({ ...s })));
      showMessage('error', 'خطا در دریافت تنظیمات');
    }
    setLoading(false);
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleValueChange = (key, newValue) => {
    const updatedData = settingsData.map(item => 
      item.key === key ? { ...item, value: parseFloat(newValue) } : item
    );
    setSettingsData(updatedData);
  };

  const handleAddNewSetting = () => {
    const newKey = prompt('نام تنظیمات جدید را وارد کنید (مثال: new_setting_comm):');
    if (!newKey) return;
    
    if (settingsData.some(s => s.key === newKey)) {
      showMessage('error', 'این نام قبلاً وجود دارد');
      return;
    }
    
    const newValue = prompt('مقدار پیش‌فرض را وارد کنید (مثال: 0.005):', '0.00000');
    if (newValue === null) return;
    
    setSettingsData([
      ...settingsData,
      { key: newKey, value: parseFloat(newValue) }
    ]);
  };

  const handleDeleteSetting = (key) => {
    if (window.confirm(`آیا از حذف تنظیم "${key}" مطمئن هستید؟`)) {
      setSettingsData(settingsData.filter(item => item.key !== key));
    }
  };

  const handleResetToDefaults = () => {
    if (window.confirm('آیا از بازنشانی تمام تنظیمات به مقادیر پیش‌فرض مطمئن هستید؟')) {
      setSettingsData(DEFAULT_SETTINGS.map(s => ({ ...s })));
      showMessage('success', 'تنظیمات به مقادیر پیش‌فرض بازنشانی شد. برای اعمال، ذخیره کنید.');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put('/api/settings', settingsData);
      showMessage('success', 'تنظیمات با موفقیت ذخیره شد');
      if (onSettingsUpdate) onSettingsUpdate();
    } catch (error) {
      console.error('Error saving settings:', error);
      showMessage('error', 'خطا در ارتباط با سرور');
    }
    setLoading(false);
  };

  const getPersianName = (key) => {
    const names = {
      'bank_interest_rate': 'نرخ سود بانکی',
      'bourse_buy_comm': 'کمیسیون خرید بورس',
      'bourse_sell_comm': 'کمیسیون فروش بورس',
      'farabourse_buy_comm': 'کمیسیون خرید فرابورس',
      'farabourse_sell_comm': 'کمیسیون فروش فرابورس',
      'leveragedfund_buy_comm': 'کمیسیون خرید صندوق اهرمی',
      'leveragedfund_sell_comm': 'کمیسیون فروش صندوق اهرمی',
      'gold_buy_comm': 'کمیسیون خرید طلا',
      'gold_sell_comm': 'کمیسیون فروش طلا',
      'future_buy_comm': 'کمیسیون خرید آتی',
      'future_sell_comm': 'کمیسیون فروش آتی',
      'option_buy_comm': 'کمیسیون خرید آپشن',
      'option_sell_comm': 'کمیسیون فروش آپشن'
    };
    return names[key] || key;
  };

  const formatKeyName = (key) => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const startEditing = (key, currentValue) => {
    setEditingKey(key);
    setEditValue(currentValue.toString());
  };

  const finishEditing = (key) => {
    const newValue = parseFloat(editValue);
    if (!isNaN(newValue)) {
      handleValueChange(key, newValue);
    }
    setEditingKey(null);
    setEditValue('');
  };

  const handleKeyPress = (e, key) => {
    if (e.key === 'Enter') {
      finishEditing(key);
    } else if (e.key === 'Escape') {
      setEditingKey(null);
      setEditValue('');
    }
  };

  return (
    <div className="settings">
      <h1>⚙️ تنظیمات نرخ‌ها</h1>
      
      {message.text && (
        <div className={`message-box ${message.type}`}>
          {message.text}
        </div>
      )}
      
      <div className="settings-container">
        <div className="settings-header">
          <div className="header-key">کلید تنظیمات</div>
          <div className="header-value">مقدار (با 5 رقم اعشار)</div>
          <div className="header-actions">عملیات</div>
        </div>
        
        <div className="settings-list">
          {settingsData.map((item) => (
            <div key={item.key} className="setting-row">
              <div className="setting-key">
                <span className="key-name" title={item.key}>
                  {getPersianName(item.key)}
                </span>
                <span className="key-code">{item.key}</span>
              </div>
              <div className="setting-value">
                {editingKey === item.key ? (
                  <input
                    type="number"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={() => finishEditing(item.key)}
                    onKeyDown={(e) => handleKeyPress(e, item.key)}
                    step="0.00001"
                    autoFocus
                    className="edit-input"
                  />
                ) : (
                  <div 
                    className="value-display"
                    onClick={() => startEditing(item.key, item.value)}
                  >
                    {item.value.toFixed(5)}
                  </div>
                )}
              </div>
              <div className="setting-actions">
                <button 
                  className="delete-btn-small"
                  onClick={() => handleDeleteSetting(item.key)}
                  title="حذف"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
        
        <div className="add-new-section">
          <button className="add-btn" onClick={handleAddNewSetting}>
            + افزودن تنظیمات جدید
          </button>
          <button className="reset-btn" onClick={handleResetToDefaults}>
            ↺ بازنشانی به مقادیر پیش‌فرض
          </button>
        </div>
        
        <div className="save-section">
          <button className="save-btn" onClick={handleSave} disabled={loading}>
            {loading ? 'در حال ذخیره...' : '💾 ذخیره تنظیمات'}
          </button>
        </div>
      </div>
      
      <div className="info-note">
        <p>📌 <strong>نکته:</strong> مقادیر کمیسیون به صورت درصد وارد می‌شوند.</p>
        <p>مثال: 0.003712 معادل 0.3712% می‌باشد.</p>
        <p>برای ویرایش هر مقدار، روی عدد کلیک کنید و مقدار جدید را وارد کنید.</p>
      </div>
    </div>
  );
}

export default Settings;